// AI kullanimi — hangi is ne kadar token/para yiyor.
//
// "Token kullanimi neden yuksek" sorusu daha once olculemiyordu: OpenRouter
// yanitlarindaki kullanim bilgisi atiliyordu. Artik her cagri kaydediliyor;
// bu ekran isleme ve modele gore toplar, en pahalisini basa koyar.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Loader2, RefreshCw } from "lucide-react";
import { authHeaders, getBackendUrl } from "../lib/http";

interface UsageItem {
  key: string;
  operation: string;
  model: string;
  kind: string;
  calls: number;
  failed: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
  costKnown: number;
  durationMs: number;
}

interface UsageRow {
  id: string;
  createdAt: string;
  operation: string;
  model: string;
  kind: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number | null;
  durationMs: number;
  ok: boolean;
  errorMessage: string | null;
}

interface Summary {
  since: string;
  items: UsageItem[];
  totals: { calls: number; failed: number; totalTokens: number; costUsd: number };
  claudeCredit?: {
    budgetUsd: number; spentUsd: number; remainingUsd: number; calls: number;
    periodStart: string; periodEnd: string;
  };
}

const OPERATION_LABEL: Record<string, string> = {
  analyze_product_image: "Ürün görseli analizi",
  analyze_source_with_a_i: "İlham listesi analizi",
  regenerate_listing_with_a_i: "Liste metni yenileme",
  generate_analysis_design: "Analizden tasarım üretimi",
  analyze_design_style: "Tasarım stili analizi",
  generate_styled_design: "Stilli tasarım üretimi",
  generate_order_design: "Sipariş tasarımı üretimi",
  analyze_concept_photo: "Konsept fotoğrafı çözümleme",
  generate_concept_shot: "Konsept görseli üretimi",
  generate_blank_mockup: "Boş mockup üretimi",
  generate_themed_mockup: "Temalı mockup üretimi",
  generate_variation: "Tasarım varyasyonu",
  analyze_design_image: "Tasarım görseli analizi",
  analyze_design_for_consistency: "Tutarlılık denetimi",
  generate_listing_seo_from_design: "Liste SEO üretimi",
  generate_lifestyle_image: "Lifestyle görseli",
  chat_text: "Metin üretimi",
  describe_video_scene: "Video sahne tarifi",
};

/** Model adindan saglayici: "saglayici/model" OpenRouter, "claude-..." dogrudan Claude API */
function ModelCell({ model }: { model: string }) {
  const provider = model.includes("/") ? "OpenRouter" : model.startsWith("claude-") ? "Claude API" : null;
  return (
    <td className="px-3 py-2 text-xs text-gray-500">
      {model}
      {provider && (
        <span className={`ml-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium ${
          provider === "Claude API" ? "bg-indigo-50 text-indigo-700" : "bg-gray-100 text-gray-600"}`}>
          {provider}
        </span>
      )}
    </td>
  );
}

const fmt = (n: number) => n.toLocaleString("tr-TR");
const usd = (n: number) => `$${n.toFixed(n < 1 ? 3 : 2)}`;

export default function AiUsagePage() {
  const [days, setDays] = useState(30);
  // Ozet mi tek tek istekler mi — "hangi cagri ne yedi" sorusu icin dokum
  const [tab, setTab] = useState<"summary" | "requests">("summary");
  const [opFilter, setOpFilter] = useState("");

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["ai-usage-summary", days],
    queryFn: async (): Promise<Summary> => {
      const r = await fetch(`${getBackendUrl()}/api/ai-usage/summary?days=${days}`, { headers: authHeaders() });
      if (!r.ok) throw new Error("Kullanım verisi alınamadı");
      return (await r.json()).data as Summary;
    },
  });

  const { data: recent = [], isLoading: recentLoading, isFetching: recentFetching, refetch: refetchRecent } = useQuery({
    queryKey: ["ai-usage-recent"],
    queryFn: async (): Promise<UsageRow[]> => {
      const r = await fetch(`${getBackendUrl()}/api/ai-usage/recent?limit=200`, { headers: authHeaders() });
      if (!r.ok) throw new Error("Kayıtlar alınamadı");
      return (await r.json()).data as UsageRow[];
    },
    enabled: tab === "requests",
  });

  const items = data?.items ?? [];
  const totals = data?.totals ?? { calls: 0, failed: 0, totalTokens: 0, costUsd: 0 };
  const maxCost = Math.max(...items.map((i) => i.costUsd), 0.000001);
  // Maliyet bilinmiyorsa (saglayici bildirmediyse) token payi gosterilir
  const maxTokens = Math.max(...items.map((i) => i.totalTokens), 1);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">
            <BarChart3 className="h-5 w-5 text-indigo-600" /> AI Kullanımı
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Her AI çağrısı kaydedilir: hangi iş, hangi model, kaç token.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value={1}>Son 24 saat</option>
            <option value={7}>Son 7 gün</option>
            <option value={30}>Son 30 gün</option>
            <option value={90}>Son 90 gün</option>
          </select>
          <button
            onClick={() => { void refetch(); if (tab === "requests") void refetchRecent(); }}
            disabled={isFetching || recentFetching}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Yenile
          </button>
        </div>
      </div>

      {data?.claudeCredit && (() => {
        const c = data.claudeCredit;
        const used = Math.min(1, c.spentUsd / c.budgetUsd);
        const day = (s: string) => new Date(s).toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
        return (
          <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-gray-700">Claude API kredisi (Max planı)</p>
              <p className="text-xs text-gray-500">{day(c.periodStart)} – {day(c.periodEnd)} dönemi</p>
            </div>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <p className="text-lg font-semibold text-emerald-700">~{usd(c.remainingUsd)} kaldı</p>
              <p className="text-sm text-gray-600">
                Harcanan {usd(c.spentUsd)} / {usd(c.budgetUsd)} · {fmt(c.calls)} çağrı
              </p>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-gray-100" role="progressbar"
              aria-label="Claude API kredisi kullanımı" aria-valuenow={Math.round(used * 100)} aria-valuemin={0} aria-valuemax={100}>
              <div className={`h-full rounded-full ${used > 0.9 ? "bg-red-500" : used > 0.7 ? "bg-amber-500" : "bg-indigo-500"}`}
                style={{ width: `${Math.round(used * 100)}%` }} />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Tahmini: yalnız bu paneldeki doğrudan Claude API çağrılarından hesaplanır (OpenRouter hariç).
              Kesin bakiye Claude Console'da: Billing → Promotional credits. Kredi bitince istekler OpenRouter'a geçer.
            </p>
          </div>
        );
      })()}

      <div className="mb-3 flex gap-1 rounded-lg bg-gray-100 p-1">
        {([["summary", "Özet"], ["requests", "İstekler"]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === k ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wider text-gray-400">Çağrı</p>
          <p className="text-lg font-semibold text-gray-900">{fmt(totals.calls)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wider text-gray-400">Token</p>
          <p className="text-lg font-semibold text-gray-900">{fmt(totals.totalTokens)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wider text-gray-400">Maliyet</p>
          <p className="text-lg font-semibold text-emerald-700">{usd(totals.costUsd)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-[11px] uppercase tracking-wider text-gray-400">Başarısız</p>
          <p className="text-lg font-semibold text-red-600">{fmt(totals.failed)}</p>
        </div>
      </div>

      {tab === "requests" ? (
        recentLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : recent.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-16 text-center">
            <p className="text-sm font-medium text-gray-600">Henüz kayıt yok</p>
          </div>
        ) : (
          <>
            <div className="mb-2 flex items-center gap-2">
              <select value={opFilter} onChange={(e) => setOpFilter(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-gray-600">
                <option value="">Tüm işlemler</option>
                {[...new Set(recent.map((r) => r.operation))].map((op) => (
                  <option key={op} value={op}>{OPERATION_LABEL[op] ?? op}</option>
                ))}
              </select>
              <span className="text-[11px] text-gray-400">Son {recent.length} istek</span>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Zaman</th>
                    <th className="px-3 py-2 text-left font-medium">İşlem</th>
                    <th className="px-3 py-2 text-left font-medium">Model</th>
                    <th className="px-3 py-2 text-right font-medium">Girdi</th>
                    <th className="px-3 py-2 text-right font-medium">Çıktı</th>
                    <th className="px-3 py-2 text-right font-medium">Toplam</th>
                    <th className="px-3 py-2 text-right font-medium">Süre</th>
                    <th className="px-3 py-2 text-right font-medium">Maliyet</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.filter((r) => !opFilter || r.operation === opFilter).map((r) => (
                    <tr key={r.id} className={`border-t border-gray-100 ${r.ok ? "" : "bg-red-50/40"}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-[11px] text-gray-500">
                        {new Date(r.createdAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-gray-800">{OPERATION_LABEL[r.operation] ?? r.operation}</span>
                        {r.kind === "image" && (
                          <span className="ml-1.5 rounded bg-pink-50 px-1.5 py-0.5 text-[10px] text-pink-600">görsel</span>
                        )}
                        {!r.ok && (
                          <span className="ml-1.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] text-red-700"
                            title={r.errorMessage ?? undefined}>hata</span>
                        )}
                      </td>
                      <ModelCell model={r.model} />
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmt(r.promptTokens)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmt(r.completionTokens)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-800">{fmt(r.totalTokens)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">{Math.round(r.durationMs / 100) / 10} sn</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">
                        {typeof r.costUsd === "number" ? usd(r.costUsd) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-16 text-center">
          <p className="text-sm font-medium text-gray-600">Bu aralıkta kayıt yok</p>
          <p className="mt-1 text-xs text-gray-400">
            Ölçüm yeni eklendi; bundan sonraki AI çağrıları burada görünür.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">İşlem</th>
                <th className="px-3 py-2 text-left font-medium">Model</th>
                <th className="px-3 py-2 text-right font-medium">Çağrı</th>
                <th className="px-3 py-2 text-right font-medium">Token</th>
                <th className="px-3 py-2 text-right font-medium">Ort. süre</th>
                <th className="px-3 py-2 text-right font-medium">Maliyet</th>
                <th className="px-3 py-2 text-left font-medium">Pay</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const share = it.costUsd > 0 ? it.costUsd / maxCost : it.totalTokens / maxTokens;
                return (
                  <tr key={it.key} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <span className="text-gray-800">{OPERATION_LABEL[it.operation] ?? it.operation}</span>
                      {it.kind === "image" && (
                        <span className="ml-1.5 rounded bg-pink-50 px-1.5 py-0.5 text-[10px] text-pink-600">görsel</span>
                      )}
                      {it.failed > 0 && (
                        <span className="ml-1.5 rounded bg-red-50 px-1.5 py-0.5 text-[10px] text-red-600">{it.failed} hata</span>
                      )}
                    </td>
                    <ModelCell model={it.model} />
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmt(it.calls)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmt(it.totalTokens)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                      {it.calls > 0 ? `${Math.round(it.durationMs / it.calls / 1000)} sn` : "-"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-emerald-700">
                      {it.costKnown > 0 ? usd(it.costUsd) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="h-1.5 w-full rounded bg-gray-100">
                        <div className="h-full rounded bg-indigo-500" style={{ width: `${Math.round(share * 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[11px] text-gray-400">
        Maliyet saglayicinin bildirdigi gercek tutardir; bildirmediginde tire görünür ve pay
        token sayısına göre hesaplanır.
      </p>
    </div>
  );
}
